/*
 * Module for one lonesome report
 */

//DataModel imported because we reqres it
define([
  "marionette",
  "backbone",
  "jquery",
  "highcharts/highcharts",
  "messaging/messenger",
  "modules/reportSplit/reports/reportArea/reportAreaView",
  "modules/reportSplit/reports/reportModel",
  "modules/reportSplit/reports/reportArea/dataModel",
  "modules/reportSplit/reports/configArea/configAreaModule",
  "modules/shared/sortableList/sortableListModule",
  "modules/shared/sortableList/sortableListModel",
  "modules/reportSplit/reports/singleReport/singleReportView",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/columnReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/pieReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/lineReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/multiLineReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/barChartShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/tableReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/customReportShow",
  "modules/reportSplit/reports/singleReport/reportDisplayRoutines/stackedColumnReportShow",
  "modules/shared/progressBar/progressBarView",
  "loading/loadingView",
  "./reportDisplayRoutines/singleNumberShow",
  "highcharts/themes/grid-light",
], function (
  Marionette,
  Backbone,
  $,
  Highcharts,
  Messenger,
  ReportAreaView,
  ReportModel,
  DataModel,
  ConfigAreaModule,
  SortableListModule,
  SortableListModel,
  SingleReportView,
  ColumnReportShow,
  PieReportShow,
  LineReportShow,
  MultiLineReportShow,
  BarChartShow,
  TableReportShow,
  CustomReportShow,
  StackedColumnReportShow,
  ProgressBarView,
  LoadingView,
  SingleNumberShow,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "singleReportModule";
      this.isOpen = false;
      this.region = options.region;
      // Adjust for the fact that Highcharts might have come from node_modules, in which case it includes a
      // "default" value at the top level that is really what we want
      const highcharts = Highcharts.default || Highcharts.Highcharts || Highcharts;
      highcharts.setOptions({
        chart: {
          style: {
            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          },
        },
        title: {
          style: {
            fontSize: "18px",
            fontWeight: "bold",
            color: "#000000",
            textTransform: "none",
          },
        },
        credits: {
          enabled: false,
        },
        exporting: {
          fallbackToExportServer: false,
        },
        lang: {
          //We can change Highcharts language options this way.
          loading: "Loading...",
          months: [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
          shortMonths: [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ],
          weekdays: [
            $.t("reports.common.sunday"),
            $.t("reports.common.monday"),
            $.t("reports.common.tuesday"),
            $.t("reports.common.wednesday"),
            $.t("reports.common.thursday"),
            $.t("reports.common.friday"),
            $.t("reports.common.saturday"),
          ],
          decimalPoint: ".",
          numericSymbols: ["k", "M", "G", "T", "P", "E"], // SI prefixes used in axis labels
          resetZoom: $.t("reports.highcharts.resetZoom"),
          resetZoomTitle: $.t("reports.higcharts.resetZoom"),
          thousandsSep: $.t("reports.highcharts.thousandsSeparator"),
          printChart: $.t("reports.highcharts.printChart"),
          downloadPNG: $.t("reports.highcharts.downloadPNG"),
          downloadJPEG: $.t("reports.highcharts.downloadJPEG"),
          downloadPDF: $.t("reports.highcharts.downloadPDF"),
          downloadSVG: $.t("reports.highcharts.downloadSVG"),
          contextButtonTitle: $.t("reports.highcharts.downloadButton"),
        },
        yAxis: {
          title: {
            style: {
              textTransform: "none",
            },
          },
        },
      });
    },
    createChart: function (options) {
      return this.layout.createChart(options);
    },
    findReport: function (reports, name) {
      var i;
      var models = reports.models;
      for (i = 0; i < models.length; i++) {
        if (models[i].get("name") === name) {
          return models[i];
        }
      }
    },
    /**
     * Handles the report-specific part of showing a report.
     * In particular, delegates to a module that will do the HighCharts (or non-HighCharts)
     * appropriate display.
     * Formerly known as showReport; renamed because there were 3 different methods in 3 different (but closely
     * related) classes named showReport, as well as a show:report reqres, and it's easier to keep track of with
     * different names.
     * @param report The report to be shown
     */
    showIndividualReport: function (report) {
      //setup common to all reports
      if (report.toJSON) {
        report = report.toJSON();
      }
      this.report = report;

      var loadingView;

      //load the single report view to be shown in the show functions
      this.layout = new SingleReportView.reportLayout();

      //the render target is the chart region of the view
      var renderTo = this.layout.chart;

      if (report.href === ReportModel.hrefs.TOPIC_LISTING_BATCH) {
        // The label and className properties are used to set the "Loading..." text and correct CSS layout
        const progressBarModel = new Backbone.Model({
          label: "reports.loading",
          className: "report-progress-loading",
        });
        loadingView = new ProgressBarView.progressBarView({ model: progressBarModel });
      } else {
        loadingView = new LoadingView.loadingView();
      }

      this.timer = setTimeout(
        (function (self) {
          //Self-executing func which takes 'this' as self
          return function () {
            //Return a function in the context of 'self'
            self.region.show(loadingView);
          };
        })(this),
        1000,
      );

      //if the module closes, don't even bother trying to show the loading view
      this.on("close", function () {
        clearTimeout(this.timer);
      });

      Messenger.vent.on(
        "report:arrived",
        function (report) {
          if (report.href === this.report.href) {
            clearTimeout(this.timer);
          }
        },
        this,
      );

      Messenger.reqres.setHandler(
        "currentReportUrl",
        function () {
          return report.href;
        },
        this,
      );

      //call the appropriate class to display the report, based on the type
      switch (report.type.toLowerCase()) {
        case "column":
          ColumnReportShow(report, renderTo, this);
          break;
        case "pie":
          PieReportShow(report, renderTo, this);
          break;
        case "line":
          LineReportShow(report, renderTo, this);
          break;
        case "multiline":
          MultiLineReportShow(report, renderTo, this);
          break;
        case "table":
          TableReportShow(report, this);
          break;
        case "stackedcolumn":
          StackedColumnReportShow(report, renderTo, this);
          break;
        case "bar":
          BarChartShow(report, renderTo, this);
          break;
        case "singlenumber":
          SingleNumberShow(report, this);
          break;
        default:
          CustomReportShow(report, renderTo, this);
          break;
      }
    },
  });
});
